/**
 * This hook creates a shared app state which is then used within the
 * App container as a context.
 *
 * Originally this was powered by useState, however it's much nicer and
 * easier DX to set and update the state with useReducer, plus allows
 * us to easily extend or create specific effects for certain actions.
 */
import { Dispatch, useReducer, useMemo, useEffect } from "react";
import objectHash from "object-hash";
import deepmerge from "deepmerge";
import { CustomEnum } from "../types";
import debug from "../utils/debug";
import {
  get,
  hasAndIsNotEmpty,
  isEmpty,
  isString,
  replaceArraysChilds
} from "../utils/helpers";
import { isProd } from "../utils/stage";
import appEvents from "../utils/appEvents";
import {
  getStoredItem,
  setStoredItem,
  getStoredItemAsIs
} from "../utils/localStorage";
import { getStoreById } from "../utils/stores";
import useT from "./useT";
import DEFAULT_CONFIG from "../config";
import { updateConfig } from "../config";
import config from "../config";

/*  const enums aren't supported in CRA+TypeScript ¯\_(ツ)_/¯
export enum AppPage {
  Setup = "setup",
  Actions = "actions",
  Search = "search",
  Customer = "customer",
  Notifications = "Notifications"
} */
export const APP_PAGE: CustomEnum<string> = Object.freeze({
  SETUP: "setup",
  ACTIONS: "actions",
  SEARCH: "search",
  CUSTOMER: "customer",
  NOTIFICATIONS: "notifications",
  HISTORY: "history"
});

export interface AppStateLocal {
  lastSetupDate: Date | string;
  lastVersion: string;
  language: string;
  zone: string;
  storeId: string;
  storeCountry: string;
  storeName: string;
  config: any;
}

export interface AppStateProperties {
  loading: boolean;
  page: string; // AppPage; // @TODO Uncomment when enums work in CRA+TS...
  salesPersonId: string;
}

export interface AppState extends AppStateLocal, AppStateProperties {}

export interface ActionSet {
  type: "set";
  payload: Partial<AppState>;
}

export interface ActionSetup {
  type: "setup";
  payload: Partial<AppStateLocal>;
}

export interface ActionSetLanguage {
  type: "setLanguage";
  payload: string;
}

export interface ActionSetZone {
  type: "setZone";
  payload: string;
}

export interface ActionSetStoreId {
  type: "setStoreId";
  payload: string;
}

export interface ActionSetStoreCountry {
  type: "setStoreCountry";
  payload: string;
}

export interface ActionSetSalesPerson {
  type: "setSalesPerson";
  payload: string;
}

export interface ActionSetPage {
  type: "setPage";
  payload: string;
}

export interface ActionToggleLoading {
  type: "toggleLoading";
  payload?: boolean;
}

export interface ActionSetConfig {
  type: "setConfig";
  payload: any;
}

export type AppStateAction =
  | ActionSet
  | ActionSetup
  | ActionSetLanguage
  | ActionSetZone
  | ActionSetStoreId
  | ActionSetStoreCountry
  | ActionSetSalesPerson
  | ActionSetPage
  | ActionToggleLoading
  | ActionSetConfig
  | { type: string; payload?: any };

export type AppStateDispatch = Dispatch<AppStateAction>;

export interface AppStateReducer {
  (state: AppState, action: AppStateAction): AppState;
}

export interface UseAppStateOptions {
  enableAsync: boolean;
}

export type UseAppState = [AppState, AppStateDispatch];

const getCurrentAppVersion = () =>
  `${process.env.REACT_APP_VERSION_COMMIT || isProd ? "UNKNOWN" : "localdev"}`;

let lang =
  (config.customise &&
    config.customise.customer &&
    config.customise.customer.defaultLang) ||
  "en";
if (lang === "emptyValue") {
  //In case the maison does not want to set an initial value for the language
  //at setupPage- it's maison's config will have the value 'emptyValue'
  //instead of a language value.
  //In that case we will leave the language value empty deductively for that maison.
  //The implication of this is that there will be no initial value for the language.
  lang = "";
}

export const DEFAULT_APP_STATE: AppState = {
  lastSetupDate: "",
  lastVersion: getCurrentAppVersion(),
  language: lang,
  zone: "",
  storeId: "",
  storeCountry: "",
  storeName: "",
  salesPersonId: "",
  loading: false,
  page: APP_PAGE.SETUP,
  config: DEFAULT_CONFIG
};

const CORRECTLY_SETUP_APP_STATE_PROPS = [
  "language",
  "zone",
  "storeId",
  "storeCountry",
  "storeName",
  "config"
];

/**
 * Checks the necessary properties on the appState object are correctly setup.
 */
export function checkAppCorrectlySetup(
  appState: AppState | AppStateLocal
): boolean {
  return (
    CORRECTLY_SETUP_APP_STATE_PROPS.reduce(
      (acc: number, propName: string) =>
        hasAndIsNotEmpty(appState, propName) ? acc + 1 : acc,
      0
    ) === CORRECTLY_SETUP_APP_STATE_PROPS.length
  );
}

/**
 * Checks if the salesPersonId has been verified correctly.
 */
export function checkHasVerifiedId(appState: AppState): boolean {
  return !isEmpty(appState.salesPersonId) && isString(appState.salesPersonId);
}

let lastUpdatedAppStateHash = "";

export function updateAppState(
  updateState: Partial<AppState>,
  prevState: AppState
) {
  const updatedState = {
    ...prevState,
    ...updateState
  };

  const updatedStateHash = objectHash(updatedState);

  // Update the local storage with the new values
  setStoredItem(
    "lastSetupDate",
    new Date(new Date().toUTCString()).toISOString()
  );
  setStoredItem("lastVersion", getCurrentAppVersion());
  setStoredItem("language", updatedState.language);
  setStoredItem("zone", updatedState.zone);
  setStoredItem("storeId", updatedState.storeId);
  setStoredItem("storeCountry", updatedState.storeCountry);
  setStoredItem("storeName", updatedState.storeName);
  setStoredItem("configApi", updatedState.config.api);

  if (
    lastUpdatedAppStateHash !== updatedStateHash &&
    checkAppCorrectlySetup(updatedState)
  ) {
    // Trigger the app event `app.setup`
    appEvents.trigger("app.setup", {
      appState: updatedState,
      prevAppState: prevState
    });
  }

  return updatedState;
}

function appStateInit(initialState?: Partial<AppState>): AppState {
  const localState = {
    lastSetupDate: getStoredItem("lastSetupDate"),
    lastVersion: getStoredItem("lastVersion", DEFAULT_APP_STATE.lastVersion),
    language: getStoredItem("language", DEFAULT_APP_STATE.language),
    zone: getStoredItem("zone", DEFAULT_APP_STATE.zone),
    storeId: getStoredItemAsIs("storeId", DEFAULT_APP_STATE.storeId),
    storeCountry: getStoredItem("storeCountry", DEFAULT_APP_STATE.storeCountry),
    storeName: getStoredItem("storeName", DEFAULT_APP_STATE.storeName),
    config: getStoredItem("config", DEFAULT_APP_STATE.config)
  };

  // Convert the lastSetupDate to a Date object
  if (localState.lastSetupDate !== "") {
    localState.lastSetupDate = new Date(localState.lastSetupDate);
  }

  const initAppState = {
    ...DEFAULT_APP_STATE,
    ...localState,
    ...initialState
  };

  return {
    ...initAppState,
    // Ensure user directed to the setup page if the app hasn't been setup correctly.
    // This will allow for developers who are messing about with the localStorage
    // parameters to easily be reminded to re-configure the app.
    page: checkAppCorrectlySetup(initAppState)
      ? APP_PAGE.ACTIONS
      : APP_PAGE.SETUP
  };
}

/**
 * AppStateDispatch action creators
 */
export const actions = {
  /**
   * Set one or many props on the appState object.
   */
  set: (payload: Partial<AppState>): ActionSet => ({
    type: "set",
    payload
  }),
  /**
   * Set only the localStorage saved props.
   */
  setup: (payload: Partial<AppStateLocal>): ActionSetup => ({
    type: "setup",
    payload
  }),
  /**
   * Set the language.
   */
  setLanguage: (payload: string): ActionSetLanguage => ({
    type: "setLanguage",
    payload
  }),
  /**
   * Set the zone.
   */
  setZone: (payload: string): ActionSetZone => ({
    type: "setZone",
    payload
  }),
  /**
   * Set the store's ID. There is an async effect which will
   * fetch the store's country based on the store data.
   */
  setStoreId: (payload: string): ActionSetStoreId => ({
    type: "setStoreId",
    payload
  }),
  /**
   * Set the store's country code (ISO 3166-1 Alpha 2).
   */
  setStoreCountry: (payload: string): ActionSetStoreCountry => ({
    type: "setStoreCountry",
    payload
  }),
  /**
   * Set the ID of the sales person/client adviser.
   */
  setSalesPerson: (payload: string): ActionSetSalesPerson => ({
    type: "setSalesPerson",
    payload
  }),
  /**
   * Set the page -- the AppRouter component will then do the rest.
   */
  setPage: (payload: string): ActionSetPage => ({
    type: "setPage",
    payload
  }),
  /**
   * Toggle the app's loading indicator. This is displayed as a
   * modal overlay.
   *
   * You can also pass a boolean to explicitly set the loading
   * state.
   */
  toggleLoading: (payload?: boolean): ActionToggleLoading => ({
    type: "toggleLoading",
    payload
  }),
  /**
   * Set config
   */
  setConfig: (payload: any): ActionSetConfig => ({
    type: "setConfig",
    payload
  })
};

export function appStateReducer(
  prevState: AppState,
  { type, payload }: AppStateAction
): AppState {
  debug("[useApp] dispatch", {
    prevState,
    type,
    payload
  });

  switch (type) {
    case "set":
    case "setup":
      return updateAppState(payload, prevState);

    case "setLanguage":
      return updateAppState({ language: payload || "en" }, prevState);

    case "setZone":
      return updateAppState({ zone: payload }, prevState);

    case "setStoreId":
      return updateAppState({ storeId: payload }, prevState);

    case "setStoreCountry":
      return updateAppState({ storeCountry: payload }, prevState);

    case "setStoreName":
      return updateAppState({ storeName: payload }, prevState);

    // The following actions don't need to trigger the `app.setup` event
    // nor need to update the localStorage
    case "setSalesPerson":
      return {
        ...prevState,
        salesPersonId: payload || ""
      };

    case "setPage":
      if (!Object.values(APP_PAGE).includes(payload)) {
        throw new Error(`Invalid APP_PAGE given at Action.payload: ${payload}`);
      }
      return {
        ...prevState,
        page: payload
      };

    case "toggleLoading":
      return {
        ...prevState,
        loading: payload !== undefined ? !!payload : !prevState.loading
      };

    case "setConfig":
      let newConfig = deepmerge(prevState.config, payload);
      // deepmerge merges array instaed of replacing them
      //   so in case of languages.supportedLanguages,
      //   the result is languages in local config + sf config
      // for that, we replace arrays completely
      replaceArraysChilds(newConfig, payload);
      updateConfig(newConfig);
      return updateAppState(
        {
          ...prevState,
          config: newConfig
        },
        prevState
      );

    default:
      throw new Error(`Invalid action type specified: ${type}`);
  }
}

/**
 * Because some of our actions rely on async functions, we wrap the
 * reducer's dispatch with some extra code that processes the action
 * before the reducer's dispatch receives the action.
 */
function asyncDispatchMiddleware(
  dispatch: Dispatch<AppStateAction>
): Dispatch<AppStateAction> {
  return action => {
    let storeId = "";
    let storeCountry = "";
    let storeName = "";
    let rest = {};

    switch (action.type) {
      // If the action is to set multiple properties in which the
      // storeId could be set as well
      case "set":
      case "setup":
        storeId = get(action, "payload.storeId");
        storeCountry = get(action, "payload.storeCountry") || "";
        storeName = get(action, "payload.storeName") || "";
        rest = { ...get(action, "payload") };
        break;

      // If the action is to set only the storeId
      case "setStoreId":
        storeId = String(action.payload);
        break;

      default:
    }

    // Only get the storeCountry if it hasn't already been set
    if (storeId && (!storeCountry || !storeName)) {
      debug(
        "[useApp] asyncDispatchMiddleware: get store details to get storeCountry",
        {
          action,
          storeId,
          storeCountry,
          rest
        }
      );

      getStoreById(storeId)
        .then(store => {
          if (store) {
            storeCountry = get(store, "geo.addressCountry") || "";
            storeName = get(store, "name") || "";

            const setStoreIdAndStoreCountry: ActionSet = {
              type: "set",
              payload: {
                ...rest,
                storeId,
                storeCountry,
                storeName
              }
            };

            debug("[useApp] found store", {
              store,
              action: setStoreIdAndStoreCountry
            });

            return dispatch(setStoreIdAndStoreCountry);
          }

          return dispatch(action);
        })
        // Do the default action if an error occurred during async
        .catch(err => {
          debug.error(err);
          return dispatch(action);
        });
    } else {
      return dispatch(action);
    }
  };
}

export default function useAppState(
  initialState: Partial<AppState> = {},
  options: UseAppStateOptions = {
    enableAsync: true
  }
): UseAppState {
  const { i18n } = useT();
  const [appState, dispatch] = useReducer(
    appStateReducer,
    appStateInit(initialState),
    appStateInit
  );

  const dispatchFn = useMemo(
    () => (options.enableAsync ? asyncDispatchMiddleware(dispatch) : dispatch),
    [options.enableAsync]
  );

  // Ensure the i18n language is loaded when the app language changes
  useEffect(() => {
    if (i18n.language && appState.language !== i18n.language) {
      i18n.changeLanguage(appState.language);
    }
  }, [i18n.language, appState.language]);

  // Trigger the `app.setup` event on init
  useEffect(() => {
    if (checkAppCorrectlySetup(appState)) {
      appEvents.trigger("app.setup", {
        appState,
        prevAppState: { ...DEFAULT_APP_STATE }
      });
    }
  }, []);

  return [appState, dispatchFn];
}
